/**
 * ブランドに登録された複数ロゴを 1 枚のシート画像 (PNG) にまとめる。
 *
 * 用途: storyboard「ロゴ画像を添付する」/ studio「ロゴを追加する」で、個別ロゴ
 * ではなくこの 1 枚を生成 API に渡す。各ロゴの属性ラベル(白バック/フルカラー等)と
 * 説明文を明記し、ブランドのロゴ体系を 1 枚で伝える。
 *
 * 設計方針 (2026-06-12 改訂): 動画生成 AI がカタログ風の装飾 (金の罫・カードの影/
 * 角丸・フッター) を誤認識するケースが多発したため、装飾を排して「上部に大きな
 * タイトル『Logos』+ 黒い細枠のロゴ枠 + その下に属性ラベル/説明」という素朴な
 * グリッドにする。属性ラベルと説明は AI にロゴ体系を伝えるために残す。
 *  - アスペクト比 4:3 (1200×900, SCALE=2)、背景は白 (非透過出力)
 *  - 透過ロゴはセル地色で非透過化 (透過×白抜き=黒セル / それ以外=白セル。
 *    {@link logoCardBackground})
 *  - 黒い細枠は画像の実寸にフィットさせて描く (固定セルではなく画像サイズに合わせ、
 *    画像と枠線の間にレターボックスの隙間を作らない)
 *  - キャプション (属性ラベル + 説明) は枠のすぐ下・白地の上に黒文字で描く
 *    (セル地色が黒でも読めるようにするため)
 */
import { getLogoAttributeLabels, logoCardBackground } from './logoAttributes';

export interface LogoSheetLogo {
  background?: string | null;
  color_type?: string | null;
  layout?: string | null;
  description?: string | null;
  logos?: { url?: string | null } | null;
}

/** 解像度スケール（見た目を保ったまま出力解像度を倍化する係数） */
const SCALE = 2;
/** 4:3。幅 1200px。 */
const CANVAS_W = 1200 * SCALE;
const CANVAS_H = 900 * SCALE;
/** キャンバス外周の余白(px) */
const OUTER_PADDING = 32 * SCALE;
/** セル間の間隔(px) */
const GUTTER = 26 * SCALE;
/** ロゴ枠下キャプションの高さ(px、属性ラベル + 説明 2 行ぶん) */
const CAPTION_H = 58 * SCALE;

/** シート地色（白。非透過出力） */
const BG_COLOR = '#ffffff';
/** ロゴ枠を区切る黒い細枠 (論理 1px) */
const CELL_BORDER_COLOR = '#000000';
const CELL_BORDER_WIDTH = 1 * SCALE;

/** シート上部のタイトル「Logos」のフォントサイズ(px) */
const TITLE_FONT_SIZE = 52 * SCALE;
const TITLE_COLOR = '#1a1a1a';
/** タイトル文言（複数ロゴを並べるシートなので英語の複数形 Logos） */
const SHEET_TITLE = 'Logos';

/** 属性ラベルのフォントサイズ(px) */
const ATTR_FONT_SIZE = 17 * SCALE;
/** 説明のフォントサイズ(px、ラベルより小さく) */
const DESC_FONT_SIZE = 13 * SCALE;
const ATTR_COLOR = '#1a1a1a';
const DESC_COLOR = '#6b6b6b';

/** URLを fetch して Image 要素にロードする（CORS taint 回避のため blob 経由） */
async function loadImage(url: string): Promise<{ img: HTMLImageElement; objectUrl: string } | null> {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    if (!blob.type.startsWith('image/')) return null;
    const objectUrl = URL.createObjectURL(blob);
    const img = new Image();
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('image load failed'));
      img.src = objectUrl;
    });
    return { img, objectUrl };
  } catch {
    return null;
  }
}

/** 1 行に収まるよう末尾を省略記号で切り詰める */
function fitText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string {
  if (ctx.measureText(text).width <= maxWidth) return text;
  const ellipsis = '…';
  let lo = 0;
  let hi = text.length;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (ctx.measureText(text.slice(0, mid) + ellipsis).width <= maxWidth) lo = mid;
    else hi = mid - 1;
  }
  return lo <= 0 ? ellipsis : text.slice(0, lo) + ellipsis;
}

interface LoadedLogo {
  img: HTMLImageElement;
  logo: LogoSheetLogo;
}

/**
 * ロゴ群を 1 枚のシート PNG blob にまとめる。
 * @returns 生成した blob と配置できたロゴ枚数。配置できるロゴが無ければ null。
 */
export async function renderBrandLogoSheetBlob(
  logos: LogoSheetLogo[],
  _meta?: { brandName?: string },
): Promise<{ blob: Blob; count: number } | null> {
  const objectUrls: string[] = [];
  const loaded: LoadedLogo[] = [];

  for (const logo of logos) {
    const url = logo.logos?.url;
    if (!url) continue;
    const res = await loadImage(url);
    if (!res) continue;
    loaded.push({ img: res.img, logo });
    objectUrls.push(res.objectUrl);
  }

  const cleanup = () => objectUrls.forEach((u) => URL.revokeObjectURL(u));

  if (loaded.length === 0) {
    cleanup();
    return null;
  }

  const canvas = document.createElement('canvas');
  canvas.width = CANVAS_W;
  canvas.height = CANVAS_H;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    cleanup();
    return null;
  }

  // 背景: 白（非透過）
  ctx.fillStyle = BG_COLOR;
  ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

  // === タイトル: 「Logos」（シート上部・大きな文字） ===
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  ctx.font = `700 ${TITLE_FONT_SIZE}px sans-serif`;
  ctx.fillStyle = TITLE_COLOR;
  ctx.fillText(SHEET_TITLE, OUTER_PADDING, OUTER_PADDING);

  // === グリッド: 各ロゴを「黒い細枠のロゴ枠 + 枠のすぐ下にキャプション」で配置 ===
  const n = loaded.length;
  const cols = Math.ceil(Math.sqrt(n));
  const rows = Math.ceil(n / cols);
  const gridLeft = OUTER_PADDING;
  // タイトルの下からグリッドを開始する
  const gridTop = OUTER_PADDING + TITLE_FONT_SIZE * 1.4;
  const gridW = CANVAS_W - OUTER_PADDING * 2;
  const gridH = CANVAS_H - gridTop - OUTER_PADDING;
  const cellW = (gridW - GUTTER * (cols - 1)) / cols;
  const cellH = (gridH - GUTTER * (rows - 1)) / rows;
  const cardH = Math.max(1, cellH - CAPTION_H);

  loaded.forEach(({ img, logo }, i) => {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const cx = gridLeft + col * (cellW + GUTTER);
    const cy = gridTop + row * (cellH + GUTTER);

    // ロゴ画像の表示サイズ: ロゴ枠領域 (cellW × cardH) に contain で収める。
    // 横は列内で中央、縦はスロット上端に揃え、その枠のすぐ下にキャプションを置く。
    const iw = img.naturalWidth || img.width;
    const ih = img.naturalHeight || img.height;
    const scale = iw && ih ? Math.min(cellW / iw, cardH / ih) : 1;
    const dw = iw && ih ? iw * scale : cellW;
    const dh = iw && ih ? ih * scale : cardH;
    const dx = cx + (cellW - dw) / 2;
    const dy = cy;

    // ロゴ枠: 画像の実寸にぴったり合わせて地色塗り + 黒い細枠で囲う（影・角丸なし）。
    // 枠を画像サイズにフィットさせることで、画像と枠線の間に余白（レターボックス）が
    // 出ない。黒バック画像は黒枠と接して一体化する。
    ctx.fillStyle = logoCardBackground(logo);
    ctx.fillRect(dx, dy, dw, dh);
    if (iw && ih) {
      ctx.drawImage(img, dx, dy, dw, dh);
    }
    ctx.strokeStyle = CELL_BORDER_COLOR;
    ctx.lineWidth = CELL_BORDER_WIDTH;
    ctx.strokeRect(dx, dy, dw, dh);

    // === キャプション: 属性ラベル + 小フォント説明（枠のすぐ下・白地に黒文字） ===
    const capX = cx;
    let capY = dy + dh + 8 * SCALE;
    const capW = cellW;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';

    const attrText = getLogoAttributeLabels(logo).join(' / ') || '属性なし';
    ctx.font = `600 ${ATTR_FONT_SIZE}px sans-serif`;
    ctx.fillStyle = ATTR_COLOR;
    ctx.fillText(fitText(ctx, attrText, capW), capX, capY);
    capY += ATTR_FONT_SIZE * 1.35;

    const desc = (logo.description ?? '').trim();
    if (desc) {
      ctx.font = `${DESC_FONT_SIZE}px sans-serif`;
      ctx.fillStyle = DESC_COLOR;
      ctx.fillText(fitText(ctx, desc, capW), capX, capY);
    }
  });

  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
  cleanup();
  if (!blob) return null;

  return { blob, count: loaded.length };
}
